/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import useActiveData from '../../hooks/use-active-data'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useAccessToken, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {StorefrontPreview} from '@salesforce/commerce-sdk-react/components'

// Chakra
import {Box, useStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import {SkipNavLink, SkipNavContent} from '@chakra-ui/skip-nav'

// Contexts
import {CurrencyProvider, StoreLocatorProvider} from '@salesforce/retail-react-app/app/contexts'

// Local Project Components
import OfflineBanner from '@salesforce/retail-react-app/app/components/offline-banner'
import OfflineBoundary from '@salesforce/retail-react-app/app/components/offline-boundary'
import ScrollToTop from '@salesforce/retail-react-app/app/components/scroll-to-top'
import Footer from '../footer'
import CheckoutHeader from '../../pages/checkout/partials/checkout-header'
import CheckoutFooter from '../../pages/checkout/partials/checkout-footer'
import AboveHeader from '@salesforce/retail-react-app/app/components/_app/partials/above-header'
import GWPModal from '../promo-callout/partials/gwp-modal'
// Hooks
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'
import {AddToCartDrawerProvider} from '../../hooks/use-add-to-cart-drawer'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import useTalkable from '../../hooks/use-talkable'

// Localization
import {IntlProvider} from 'react-intl'

// Others
import {watchOnlineStatus, isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {getTargetLocale, fetchTranslations} from '@salesforce/retail-react-app/app/utils/locale'
import {
    THEME_COLOR,
    DEFAULT_LOCALE,
    ACTIVE_DATA_ENABLED,
    DEFAULT_SITE_TITLE_YR,
    DEFAULT_SITE_TITLE_KS
} from '../../constants'

import Seo from '../../components/seo'
import {Helmet} from 'react-helmet'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

import AssistiveADA from '../assistive-ada'
import UtilityBanner from '../utility-banner'
import SessionAiBanner from '../session-ai-banner'
import LtkFooterSubscribe from '../listrak-script'
import GtmScript from '../tag-manager/gtm-script'
import IAdvizeScript from '../iadvize'
import OneTrustScript from '../one-trust'
import AttentiveScript from '../attentive-script'
import Bazaarvoice from '../bazaarvoice'
import IdmeScript from '../idme-script'
import TalkableScript from '../talkable-script'
import MonetateScript from '../monetate'
import RiskifiedScript from '../riskified-script'
import BounceExchange from '../bounce-exchange'
import PopupAnalytics from '../popup-analytics'
import StorefrontToolkitConnector from '../storefront-toolkit-connector'
import {getUrl} from '../../utils/cloudinary-utils'
import {StoreProvider} from '../../contexts/store'
import {SiteProvider} from '../../contexts/site'
import PurpleDotInit from '../purple-dot/init'
import BrowserScrollbarWidth from '../browser-scrollbar-width'
import GlobalEScript from '../globale-script'
import {GlobaleProvider} from '../../contexts/globale'
import HeaderPromoBar from '../header/header-promo-bar'
import HeaderWrapper from '../header/header-wrapper'
import Veil from '../../pages/product-list/partials/veil'
import {BonusProductSelectionModalProvider} from '@salesforce/retail-react-app/app/hooks/use-bonus-product-selection-modal'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {getOrCreateBrUid2} from '../../utils/common-utils'

const App = (props) => {
    const {children} = props
    const location = useLocation()
    const isYR = location?.pathname?.includes('/yellow-rose')
    const {getTokenWhenReady} = useAccessToken()
    const queryClient = useQueryClient()
    const appOrigin = useAppOrigin()
    const activeData = useActiveData()
    const authModal = useAuthModal()
    const {site, locale, buildUrl} = useMultiSite()
    const [isOnline, setIsOnline] = useState(true)
    const styles = useStyleConfig('App')
    const navigate = useNavigation()

    const targetLocale = getTargetLocale({
        getUserPreferredLocales: () => {
            // CONFIG: This function should return an array of preferred locales. They can be
            // derived from various sources. Below are some examples of those:
            //
            // - client side: window.navigator.languages
            // - the page URL they're on (example.com/en-GB/home)
            // - cookie (if their previous preference is saved there)
            //
            // If this function returns an empty array (e.g. there isn't locale in the page url),
            // then the app would use the default locale as the fallback.

            // NOTE: Your implementation may differ, this is just what we did.
            return [locale?.id || DEFAULT_LOCALE]
        },
        l10nConfig: site.l10n
    })

    useEffect(() => {
        /** Not a reactJS practice - This is used to handle redirections in the content asset within the domain **/
        document.addEventListener('click', function (e) {
            if (
                e.target?.classList?.contains('redirect-link') &&
                e?.target?.getAttribute('data-url')
            ) {
                navigate(e.target.getAttribute('data-url'))
            }
        })
    }, [])

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                const orderInProgress = localStorage.getItem('orderInProgress')
                if (orderInProgress && orderInProgress != 'null') {
                    const paymentInProgress = localStorage.getItem('paymentInProgress')
                    if (paymentInProgress === 'paypal') {
                        setTimeout(() => {
                            // re-read latest values at fire time, not the stale ones from above
                            const latestOrderInProgress = localStorage.getItem('orderInProgress')
                            const latestPaymentInProgress =
                                localStorage.getItem('paymentInProgress')

                            if (
                                latestOrderInProgress &&
                                latestOrderInProgress !== 'null' &&
                                latestPaymentInProgress === 'paypal' &&
                                !document.querySelector('.paypal-checkout-sandbox-iframe')
                            ) {
                                // We do this 'only' for the case where the user closes the PayPal modal on iOS devices,
                                // because iOS does not invoke the PayPal `onCancel` callback in that scenario.
                                window.location.reload()
                            }
                        }, 1500)
                    }
                    return
                }
                queryClient.invalidateQueries({
                    predicate: (query) =>
                        query.queryKey.some(
                            (segment) => typeof segment === 'string' && segment.includes('/baskets')
                        )
                })
            }
        }
        document.addEventListener('visibilitychange', handleVisibilityChange)
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
    }, [queryClient])

    // If the translation file exists, it'll be served directly from static folder (and won't reach this code here).
    // However, if the file is missing, the App would render a 404 page.
    const is404ForMissingTranslationFile = /\/static\/translations\/compiled\/[^.]+\.json$/.test(
        location?.pathname
    )

    // Fetch the translation message data using the target locale.
    const {data: messages} = useQuery({
        queryKey: ['app', 'translations', 'messages', targetLocale],
        queryFn: () => {
            if (is404ForMissingTranslationFile) {
                // Return early to prevent an infinite loop
                // Otherwise, it'll continue to fetch the missing translation file again
                return {}
            }
            return fetchTranslations(targetLocale, appOrigin)
        },
        enabled: isServer
    })

    // Used to conditionally render header/footer for checkout page
    const isCheckout = location?.pathname?.replace(/\/$/, '') === '/checkout'
    const {l10n} = site
    // Get the current currency to be used through out the app
    const currency = locale.preferredCurrency || l10n.defaultCurrency

    // Create the _br_uid_2 cookie before bloomreach needs it

    useEffect(() => {
        if (!onClient()) return

        const cookie = new CookieStorage({
            keySuffix: '',
            sharedContext: false
        })

        getOrCreateBrUid2(cookie)
    }, [])
    // Handle creating a new basket if there isn't one already assigned to the current
    // customer.
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()

    const updateBasket = useShopperBasketsMutation('updateBasket')
    const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket')
    useEffect(() => {
        // update the basket currency if it doesn't match the current locale currency
        if (basket?.currency && basket?.currency !== currency) {
            updateBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {currency}
            })
        }
    }, [basket?.currency])

    useEffect(() => {
        // update the basket customer email
        if (
            basket &&
            customer?.isRegistered &&
            customer?.email &&
            customer?.email !== basket?.customerInfo?.email
        ) {
            updateCustomerForBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {
                    email:
                        customer?.isRegistered && basket?.c_ListrakSignUpEmail
                            ? basket?.c_ListrakSignUpEmail
                            : customer.email
                }
            })
        }
    }, [customer?.isRegistered, customer?.email, basket?.customerInfo?.email])

    useEffect(() => {
        const value = String(!!customer?.isRegistered)

        document.documentElement.setAttribute('data-is-registered', value)
        document.body.setAttribute('data-is-registered', value)
    }, [customer?.isRegistered])

    useEffect(() => {
        // Listen for online status changes.
        watchOnlineStatus((isOnline) => {
            setIsOnline(isOnline)
        })
    }, [])

    const trackPage = () => {
        activeData.trackPage(site.id, locale.id, currency)
    }

    useEffect(() => {
        trackPage()
    }, [location])

    const {
        app: {commerceAPI}
    } = getConfig()
    const {storefrontPreferences} = commerceAPI.storefront || {}
    let googleSiteVerificationTag = null
    let showMaintenancePage = false
    try {
        if (storefrontPreferences) {
            const customPrefsAsJson = JSON.parse(storefrontPreferences)
            googleSiteVerificationTag = customPrefsAsJson.googleSiteVerificationTag
            showMaintenancePage = customPrefsAsJson.showMaintenancePage || false
        }
    } catch (e) {
        console.log(e)
    }

    useEffect(() => {
        if (showMaintenancePage) {
            window.location.href = window.location.origin + '/maintenance'
        }
    }, [showMaintenancePage])

    const talkable = useTalkable()
    useEffect(() => {
        talkable.authenticateCustomer()
    }, [customer?.isRegistered])

    return (
        <Box className="sf-app" {...styles.container}>
            <Veil />
            <BrowserScrollbarWidth />
            <StorefrontToolkitConnector getToken={getTokenWhenReady}>
                <Helmet>
                    {ACTIVE_DATA_ENABLED && (
                        <script
                            src={getAssetUrl('static/head-active_data.js')}
                            id="headActiveData"
                            type="text/javascript"
                            defer
                        ></script>
                    )}
                </Helmet>
                <StorefrontPreview getToken={getTokenWhenReady}>
                    <IntlProvider
                        onError={(err) => {
                            if (!messages) {
                                // During the ssr prepass phase the messages object has not loaded, so we can suppress
                                // errors during this time.
                                return
                            }
                            if (err.code === 'MISSING_TRANSLATION') {
                                // NOTE: Remove the console error for missing translations during development,
                                // as we knew translations would be added later.
                                logger.warn('Missing translation', {
                                    namespace: 'App.IntlProvider',
                                    additionalProperties: {
                                        errorMessage: err.message
                                    }
                                })
                                return
                            }
                            throw err
                        }}
                        locale={targetLocale}
                        messages={messages}
                        // For react-intl, the _default locale_ refers to the locale that the inline `defaultMessage`s are written for.
                        // NOTE: if you update this value, please also update the following npm scripts in `template-retail-react-app/package.json`:
                        // - "extract-default-translations"
                        // - "compile-translations:pseudo"
                        defaultLocale={DEFAULT_LOCALE}
                    >
                        <CurrencyProvider currency={currency}>
                            <BonusProductSelectionModalProvider>
                                <StoreLocatorProvider>
                                    <SiteProvider>
                                        <GlobaleProvider>
                                            <StoreProvider>
                                                <Seo>
                                                    {googleSiteVerificationTag && (
                                                        <meta
                                                            name="google-site-verification"
                                                            content={googleSiteVerificationTag}
                                                        />
                                                    )}
                                                    <meta
                                                        name="theme-color"
                                                        content={THEME_COLOR}
                                                    />
                                                    <meta
                                                        name="apple-mobile-web-app-title"
                                                        content={
                                                            isYR
                                                                ? DEFAULT_SITE_TITLE_YR
                                                                : DEFAULT_SITE_TITLE_KS
                                                        }
                                                    />
                                                    <link
                                                        rel="apple-touch-icon"
                                                        href={getAssetUrl(
                                                            isYR
                                                                ? 'static/img/global/yr-apple-touch-icon.png'
                                                                : 'static/img/global/ks-apple-touch-icon.png'
                                                        )}
                                                    />
                                                    <link
                                                        rel="icon"
                                                        type="image/png"
                                                        href={getUrl(
                                                            isYR
                                                                ? 'UIImages/images/yr-favicon.ico'
                                                                : 'UIImages/images/favicon.ico'
                                                        )}
                                                    />
                                                    <link
                                                        rel="manifest"
                                                        href={getAssetUrl(
                                                            isYR
                                                                ? 'static/yr-manifest.json'
                                                                : 'static/ks-manifest.json'
                                                        )}
                                                    />
                                                    <link
                                                        rel="manifest"
                                                        href={getAssetUrl('static/manifest.json')}
                                                    />

                                                    {/* Urls for all localized versions of this page (including current page)
                                For more details on hrefLang, see https://developers.google.com/search/docs/advanced/crawling/localized-versions */}
                                                    {site.l10n?.supportedLocales.map((locale) => (
                                                        <link
                                                            rel="alternate"
                                                            hrefLang={locale.id.toLowerCase()}
                                                            href={`${appOrigin}${buildUrl(
                                                                location.pathname
                                                            )}`}
                                                            key={locale.id}
                                                        />
                                                    ))}
                                                    {/* A general locale as fallback. For example: "en" if default locale is "en-GB" */}
                                                    <link
                                                        rel="alternate"
                                                        hrefLang={site.l10n.defaultLocale.slice(
                                                            0,
                                                            2
                                                        )}
                                                        href={`${appOrigin}${buildUrl(
                                                            location.pathname
                                                        )}`}
                                                    />
                                                    {/* A wider fallback for user locales that the app does not support */}
                                                    <link
                                                        rel="alternate"
                                                        hrefLang="x-default"
                                                        href={`${appOrigin}/`}
                                                    />
                                                </Seo>

                                                <ScrollToTop />

                                                <Box
                                                    id="app"
                                                    display="flex"
                                                    flexDirection="column"
                                                    flex={1}
                                                >
                                                    <SkipNavLink zIndex="skipLink">
                                                        Skip to Content
                                                    </SkipNavLink>
                                                    <AssistiveADA />
                                                    <OneTrustScript />
                                                    <GlobalEScript />
                                                    <MonetateScript />
                                                    <Bazaarvoice />
                                                    <BounceExchange />
                                                    <PopupAnalytics />
                                                    <PurpleDotInit />
                                                    {!isCheckout && <UtilityBanner />}
                                                    <IdmeScript />
                                                    <RiskifiedScript />
                                                    <TalkableScript />
                                                    <Box
                                                        {...styles.headerWrapper}
                                                        className="header-wrapper"
                                                    >
                                                        {!isCheckout ? (
                                                            <>
                                                                <AboveHeader />
                                                                <HeaderWrapper />
                                                            </>
                                                        ) : (
                                                            <CheckoutHeader />
                                                        )}
                                                    </Box>
                                                    {!isCheckout && (
                                                        <HeaderPromoBar
                                                            key={isYR ? 'YR' : 'KS'}
                                                            brand={isYR ? 'YR' : 'KS'}
                                                        />
                                                    )}
                                                    <SessionAiBanner />
                                                    <LtkFooterSubscribe />
                                                    <IAdvizeScript />
                                                    <GtmScript />
                                                    <AttentiveScript />
                                                    {!isOnline && <OfflineBanner />}
                                                    <AddToCartDrawerProvider>
                                                        <SkipNavContent
                                                            style={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                flex: 1,
                                                                outline: 0
                                                            }}
                                                        >
                                                            <Box
                                                                as="main"
                                                                id="app-main"
                                                                role="main"
                                                                display="flex"
                                                                flexDirection="column"
                                                                flex="1"
                                                            >
                                                                <OfflineBoundary isOnline={false}>
                                                                    {children}
                                                                </OfflineBoundary>
                                                            </Box>
                                                        </SkipNavContent>

                                                        {!isCheckout ? (
                                                            <Footer />
                                                        ) : (
                                                            <CheckoutFooter />
                                                        )}

                                                        <AuthModal {...authModal} />
                                                        {!isServer && <GWPModal />}
                                                    </AddToCartDrawerProvider>
                                                </Box>
                                            </StoreProvider>
                                        </GlobaleProvider>
                                    </SiteProvider>
                                </StoreLocatorProvider>
                            </BonusProductSelectionModalProvider>
                        </CurrencyProvider>
                    </IntlProvider>
                </StorefrontPreview>
                {ACTIVE_DATA_ENABLED && (
                    <script
                        src={getAssetUrl('static/dwac-21.7.js')}
                        type="text/javascript"
                        id="dwac"
                        async="async"
                    ></script>
                )}
                {ACTIVE_DATA_ENABLED && (
                    <script
                        type="text/javascript"
                        src={getAssetUrl('static/dwanalytics-22.2.js')}
                        id="dwanalytics"
                        async="async"
                        onLoad={trackPage}
                    ></script>
                )}
            </StorefrontToolkitConnector>
        </Box>
    )
}

App.propTypes = {
    children: PropTypes.node
}

export default App
